import React, { useEffect, useCallback, useMemo, useRef } from "react";
import { DataGrid } from "../../primitives";
import CustomPagination from "../../Pagination/CustomPagination";
import ERPSBaseButton from "../inputs/ERPSBaseButton";
import useDataGridFocus from "../../../hooks/useDataGridFocus";
import { ResponsiveBox } from "../../primitives";

const ERPSGrid = (props) => {
  const {
    data,
    history,
    pathName,
    onAnyPaginationAction,
    hideFooterPagination,
    dataGridWidth,     // this is the name of css class
    gridBoxWidth,      // a number is used as grid with is pixels; otherwise any css width (e.g. "1000px" or "95%").  Ignored if sumOfColumnWidth is true.
    sumOfColumnWidth,  // if true, the Grid width will be calculated as the sum of column widths.
    initialLoading = false,
    userRole,
    componentAccess,
    handleSelectionModelChange,
    checkboxSelection,
    handleCellEditCommit,
    selectedRows,
    isRowSelectable,
    initialStates,
    getCellClassName,
    autoRowHeight,
    tableLabelText,
    pageName,
    nextBtnLoading,
    prevBtnLoading,
  } = props;
  
  // Memoize rows, columns, and count to prevent unnecessary re-renders
  // Extract from data prop and memoize to maintain stable references
  // This prevents DataGrid re-render when only rowsPerPage state changes
  const columns = useMemo(() => data?.columns || [], [data?.columns]);
  const rows = useMemo(() => data?.rows || [], [data?.rows]);
  const count = useMemo(() => data?.count || 0, [data?.count]);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    hideFooterPagination ? count : props?.rowPerPage || 25
  );
  const [loading, setLoading] = React.useState(false);
  useEffect(() => {
    if (props?.pageNumber !== undefined) {
      setPage(props?.pageNumber);
    }
  }, [props?.pageNumber]);

  useEffect(() => {
    if (props?.rowPerPage) {
      setRowsPerPage(props?.rowPerPage);
    }
  }, [props?.rowPerPage]);

  // WCAG 2.4.3: Focus first interactive element in table when tabbing to table
  // Skip table header and focus first interactive element (link, button, input, etc.) instead
  useDataGridFocus({ rows, columns, dataGridWidth });

  const clickableColumns = ["", "billerId"];
  const clickableColumnsToCheckWithRole = ["", ""];
  const handleOnCellClick = useCallback((params) => {
    const { field, value } = params;
    const hasCellClickable =
      userRole !== "CE" && clickableColumnsToCheckWithRole.includes(field);
    let userComponentChildAccess = {};
    if (field === "userId") {
      userComponentChildAccess = componentAccess.find(
        ({ componentType }) => componentType === "MUGFV"
      );
    }

    if (clickableColumns.includes(field) || hasCellClickable) {
      history.push(pathName, {
        id: value,
        componentAccess: userComponentChildAccess?.childComponents,
      });
    }
  }, [userRole, componentAccess, history, pathName]);

  const handleChangePage = useCallback((event, newPage, actionType) => {
    setLoading(true);
    onAnyPaginationAction({
      ...data,
      startOffset: newPage * rowsPerPage,
      endOffset: newPage * rowsPerPage + rowsPerPage,
      actionType,
    });
    setPage(newPage);
    if (props?.setPage) {
      props?.setPage(newPage);
    }
    setLoading(false);
  }, [data, rowsPerPage, onAnyPaginationAction, props?.setPage]);

  const handleChangeRowsPerPage = useCallback((event) => {
    const newRowsPerPage = event.target.value;
    setRowsPerPage(newRowsPerPage);
    onAnyPaginationAction({
      ...data,
      startOffset: 0,
      endOffset: newRowsPerPage,
    });
    setPage(0);
  }, [data, onAnyPaginationAction]);

  const getRowHeight = useCallback(() => {
    return (autoRowHeight == true) ? "auto" : null;
  }, [autoRowHeight]);

  // Stable empty function to prevent unnecessary re-renders
  const handleEmptySelectionChange = useCallback(() => {}, []);

  const boxProperties = useMemo(() => {
    let properties = {};
    if (dataGridWidth) {
      properties.className = dataGridWidth;
    }
    if (sumOfColumnWidth === true || sumOfColumnWidth === "true") {
      // 50 is minimum column width (even if the setting is less).  2 pixels added for table outside border.
      properties.sx = { width: columns.map(c => Math.max(50, c.width)).reduce((a,b) => a+b, 2) + "px" }; 
    } else if (gridBoxWidth) {
      let boxWidth = (/^\d+$/.test(gridBoxWidth)) /* if integer value */ ? gridBoxWidth + "px" : gridBoxWidth;
      properties.sx = { width: boxWidth};
    }
    return properties;
  }, [dataGridWidth, sumOfColumnWidth, columns, gridBoxWidth]);

  // Use refs to store current pagination values
  // This allows the Pagination component to have a stable reference
  // while always reading the latest values
  const paginationRef = useRef({
    page,
    rowsPerPage,
    count,
    handleChangePage,
    handleChangeRowsPerPage,
  });

  // Update ref whenever values change
  useEffect(() => {
    paginationRef.current = {
      page,
      rowsPerPage,
      count,
      handleChangePage,
      handleChangeRowsPerPage,
    };
  }, [page, rowsPerPage, count, handleChangePage, handleChangeRowsPerPage]);

  // Create a stable Pagination component that reads from refs
  // This prevents DataGrid from re-rendering when only pagination values change
  const StablePaginationComponent = useCallback(() => {
    const current = paginationRef.current;
    return (
      <CustomPagination
        page={current.page}
        rowsPerPage={current.rowsPerPage}
        count={current.count}
        handleChangePage={current.handleChangePage}
        handleChangeRowsPerPage={current.handleChangeRowsPerPage}
      />
    );
  }, []); // Empty deps - component reference never changes

  // Memoize components object with stable Pagination component
  // This object reference only changes if StablePaginationComponent changes (which it never does)
  const paginationComponents = useMemo(() => ({
    Pagination: StablePaginationComponent,
  }), [StablePaginationComponent]);

  return (
    <ResponsiveBox {...boxProperties}>
      <DataGrid
        onSelectionModelChange={handleSelectionModelChange}
        disableSelectionOnClick
        loading={initialLoading || loading}
        onCellClick={handleOnCellClick}
        rowHeight={65}
        getRowHeight={getRowHeight}
        checkboxSelection={checkboxSelection}
        selectionModel={selectedRows}
        onSelectionChange={handleEmptySelectionChange}
        autoHeight
        hideFooterPagination={hideFooterPagination}
        density="compact"
        hideFooter={hideFooterPagination}
        aria-label={tableLabelText}
        disableColumnMenu
        hideFooterSelectedRowCount
        rows={rows}
        columns={columns}
        pageSize={rowsPerPage}
        showPaginationBottom={!hideFooterPagination}
        onCellEditCommit={handleCellEditCommit}
        isRowSelectable={isRowSelectable}
        initialState={initialStates}
        getCellClassName={getCellClassName}
        components={paginationComponents}
      />
      {pageName === "transaction-enquiry" && hideFooterPagination && (
        <ResponsiveBox display="flex" justifyContent={"end"} mr={20}>
          {page !== 0 ? (
            <ERPSBaseButton
              variant="secondary"
              className="button_with_margin48"
              label={"Previous"}
              onClick={(e) => handleChangePage(e, page - 1, "prev")}
              loading={prevBtnLoading}
            />
          ) : (
            ""
          )}
          {!(rows.length <= 100) ? (
            <ResponsiveBox pl={2}>
              <ERPSBaseButton
                variant="primary"
                className="button_with_margin48"
                label={"Next"}
                onClick={(e) => handleChangePage(e, page + 1, "next")}
                loading={nextBtnLoading}
              />
            </ResponsiveBox>
          ) : (
            ""
          )}
        </ResponsiveBox>
      )}
    </ResponsiveBox>
  );
};

export default ERPSGrid;
